customElements.whenDefined('search-form').then(() => {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', debounce((event) => { this.onChange(event);}, 300).bind(this));
      this.productTypes?.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.closest('.template-search__search') || this.closest('.header__search-bar')) {
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      this.addEventListener('click', (event) => {
        if (event.target.matches('.inline-search__close-button') || event.target.closest('.inline-search__close-button')) {
          this.onFormReset();
          this.close();
          document.activeElement.blur();
          setTimeout(() => { document.activeElement.blur(); }, 500);
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.classList.add('opacity-20');
      }

      if(newSearchTerm.length === 0){
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (window.visualViewport) {
        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 100);

        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 500);
      }

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
          (element) => element.offsetParent !== null
      );
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    // Build URL params with product type filter
    buildSearchParams(searchTerm) {
      const formData = new FormData(document.querySelector('.predictive_search_form') || document.querySelector('.predictive_search_page_form'));
      const params = new URLSearchParams(formData);

      if (this.productTypes && this.productTypes.value.length > 0) {
        searchTerm = `product_type:${this.productTypes.value} AND ${searchTerm}`;
      }

      params.set('q', searchTerm);
      params.set('resources[options][fields]', 'title,product_type,vendor,tag');

      return params.toString();
    }

    // Keep only result items whose visible text contains the search term exactly (case-insensitive).
    // Prevents fuzzy/partial Shopify matches from showing up in the dropdown.
    filterExactMatches(html, term) {
      const needle = term.toLowerCase().trim();
      if (!needle) return html;

      const div = document.createElement('div');
      div.innerHTML = html;

      div.querySelectorAll('li').forEach(item => {
        if (!item.textContent.toLowerCase().includes(needle)) {
          item.remove();
        }
      });

      return div.innerHTML;
    }

    // Extract series prefix from hyphenated model numbers.
    // "14-ac104LA" → "14-ac"   "HP-15s-fq2028la" → "HP-15s-fq"   "14-an013la" → "14-an"
    // Returns null when term has no numeric suffix (no prefix search needed).
    extractModelPrefix(term) {
      const parts = term.split('-');
      if (parts.length < 2) return null;

      const lastPart = parts[parts.length - 1];
      // Segment qualifies as a model suffix when it contains 3+ consecutive digits
      const m = lastPart.match(/^([a-z]*)\d{3,}/i);
      if (!m) return null;

      if (m[1]) {
        // Keep letter portion of last segment: "ac104LA" → "ac"
        parts[parts.length - 1] = m[1];
      } else {
        // Last segment is pure digits: remove it entirely
        parts.pop();
        if (parts.length === 0) return null;
      }

      const prefix = parts.join('-');
      return prefix !== term ? prefix : null;
    }

    // Fetch predictive search and return the rendered HTML markup
    fetchSearchMarkup(urlParams) {
      return fetch(`${routes.predictive_search_url}?${urlParams}`, {
        signal: this.abortController.signal,
      })
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(text => {
        return new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
      });
    }

    // Merge prefix results into primary results, deduplicating by product URL path
    mergeResultsHTML(primaryHTML, prefixHTML) {
      const wrap = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div;
      };

      const primary = wrap(primaryHTML);
      const secondary = wrap(prefixHTML);

      // Collect product paths already in primary results
      const existingPaths = new Set(
        Array.from(primary.querySelectorAll('a[href*="/products/"]'))
          .map(a => a.getAttribute('href').split('?')[0])
      );

      // If primary returned no products, show prefix results directly
      if (existingPaths.size === 0) return prefixHTML;

      // Append unique products from prefix results to primary list
      const primaryList = primary.querySelector('ul');
      if (!primaryList) return primaryHTML;

      secondary.querySelectorAll('li').forEach(item => {
        const link = item.querySelector('a[href*="/products/"]');
        if (!link) return;
        const path = link.getAttribute('href').split('?')[0];
        if (!existingPaths.has(path)) {
          primaryList.appendChild(item.cloneNode(true));
          existingPaths.add(path);
        }
      });

      return primary.innerHTML;
    }

    getSearchResults(searchTerm) {
      const prefix = this.extractModelPrefix(searchTerm);
      const urlParams = this.buildSearchParams(searchTerm);
      const queryKey = urlParams.toLowerCase();

      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      if (prefix) {
        // Fetch full term and series prefix in parallel, then merge results
        const prefixParams = this.buildSearchParams(prefix);

        Promise.all([
          this.fetchSearchMarkup(urlParams),
          this.fetchSearchMarkup(prefixParams),
        ])
        .then(([primaryHTML, prefixHTML]) => {
          // Filter each result set by its own term before merging
          const filtered = this.mergeResultsHTML(
            this.filterExactMatches(primaryHTML, searchTerm),
            this.filterExactMatches(prefixHTML, prefix)
          );
          this.allPredictiveSearchInstances.forEach(inst => {
            inst.cachedResults[queryKey] = filtered;
          });
          this.renderSearchResults(filtered);
        })
        .catch(error => {
          if (error?.code === 20) return;
          this.close();
          throw error;
        });
      } else {
        this.fetchSearchMarkup(urlParams)
        .then(resultsMarkup => {
          const filtered = this.filterExactMatches(resultsMarkup, searchTerm);
          this.allPredictiveSearchInstances.forEach(inst => {
            inst.cachedResults[queryKey] = filtered;
          });
          this.renderSearchResults(filtered);
        })
        .catch(error => {
          if (error?.code === 20) return;
          this.close();
          throw error;
        });
      }
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.querySelector('.search-suggestions')?.classList.add('hidden');

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
      if (window.updateViewportSize) window.updateViewportSize();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
      }
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});
